import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Prompt, StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import dealsStore from '../../store/dealsStore';
import DealsItems from '../DealsItems/DealsItems';
import DealsMainDetails from '../DealsMainDetails/DealsMainDetails';
import { SCREENS } from 'src/constants/screens';
import { localeStore } from 'src/mobx/localesStore';
import { getTranslate, scrollToErrorElement } from 'src/utils/helpers';
import { confirmZeroPrice } from 'src/utils/modalConfirmations';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import { getPromptProps } from 'src/view/components/CustomPrompt/helper';
import ItemImages from 'src/view/components/ItemImages/ItemImages';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';
import HeaderBack from 'src/view/containers/Headers/components/HeaderBack/HeaderBack';
import Headers from 'src/view/containers/Headers/Headers';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import MenuManagTabItemPrice from 'src/view/screens/MenuManagementScreen/containers/MenuManagTabItemPrice/MenuManagTabItemPrice';
import { BUTTON_TITLE } from '~/constants/main';
import './style.scss';

type IProps = RouteComponentProps<{ id?: string }, StaticContext, undefined>;

const DealsEdit: React.FC<IProps> = observer((props: IProps): JSX.Element => {
  const isZeroPrice = useMemo((): boolean => {
    return Boolean(dealsStore.deal?.items?.some((item) => Number(item.price) === 0) || Number(dealsStore.deal?.price) === 0);
  }, [dealsStore.deal?.price, dealsStore.deal?.items]);

  useEffect(() => {
    if (!dealsStore.dealsCount) dealsStore.fetchDealsCount();

    if (props.match.params.id) {
      dealsStore.setDeal(dealsStore.pagination.data.find((deal) => deal.id === Number(props.match.params.id)));
      if (!dealsStore.deal?.id) dealsStore.getDeal(props.match.params.id);
      return;
    }

    dealsStore.setDeal();
  }, []);

  const clickSave = () => {
    if (!dealsStore.deal) return;
    dealsStore.setValidation();

    if (!dealsStore.validation.valid) {
      scrollToErrorElement();
      return;
    }
    if (isZeroPrice) {
      confirmZeroPrice({ onConfirm: save });
      return;
    }

    save();
  };

  const save = () => {
    const { deal, createDeal, updateDeal } = dealsStore;
    if (!deal) return;

    if (deal.is_draft) {
      createDeal();
    } else {
      updateDeal(deal.id);
    }
  };

  const changePrice = useCallback((price: string) => {
    dealsStore.changeDealHandler({ price });
    // update items discount prices
    dealsStore.recalculationDealItemPrice();
    // update validation
  }, []);

  return (
    <>
      <Headers>
        <HeaderBack title={dealsStore.deal ? getTranslate(dealsStore.deal.name) : ''} noTranslation path={SCREENS.DEALS()} />
      </Headers>
      <ScrollableBlock showScrollbar className="full-height mt-6">
        <ItemImages
          currentSaveImage={dealsStore.deal?.image}
          currentSaveImages={dealsStore.deal?.images}
          //
          images={dealsStore.images}
          validation={dealsStore.validation.images}
          draggableImage={dealsStore.draggableImage}
          uploadImage={dealsStore.uploadImage}
          deleteImage={dealsStore.deleteImage}
          imagesChangesSubmit={dealsStore.imagesChangesSubmit}
          setValidation={dealsStore.setValidation}
          setDraggableImage={dealsStore.setDraggableImage}
          handleDragEnd={dealsStore.handleDragEnd}
        />
        <DealsMainDetails />
        <DealsItems />
        <MenuManagTabItemPrice
          price={dealsStore.deal?.price}
          title="combo_offer_price"
          validation={dealsStore.validation.price}
          changePrice={changePrice}
          setValidation={() => dealsStore.setValidation()}
          headerClassName="deals-edit__input mb-2"
          inputClassName="deals-edit__input"
          wrapperClassName="mt-6"
        />
        <Button title={localeStore.t(BUTTON_TITLE.SAVE)} onClick={clickSave} loading={dealsStore.isLoading} className="mt-8" />
      </ScrollableBlock>
      <LoadingOverlay visible={dealsStore.isLoading} />
      <Prompt when={dealsStore.edited} message={getPromptProps()} />
    </>
  );
});

export default DealsEdit;
